// src/lib/managers/conversation/types/conversation.ts

import { IDateValue } from "../../../utils";
import {
    AgendaItem,
    AgendaItemAttendee,
    AgendaItemDateTime,
    AgendaItemLocation,
    AgendaItemOrganizer,
    AgendaTimeSlot,
    CreateAgendaItemData,
    OnlineMeeting,
} from "../../agenda";
import type {
    AssistantProjectDTO,
    AssistantProjectInputDTO,
    ConversationSummaryDTO,
} from "../../api/types/apiResponse";
import { CustomViewSortOrder, ViewFilter } from "../../customView";
import { Mail, MailSender, SmartLabel, TodoItem } from "../../mail";

export type Message = {
    text: string;
    role: "user" | "assistant" | "system" | "tool";
};

export enum ComponentDataContentType {
    Mails = "Mails",
    FallBackResponse = "FallBackResponse",
    BasicAiChat = "BasicAiChat",
    Error = "Error",
    AgendaItems = "AgendaItems",
    CreateAgendaItem = "CreateAgendaItem",
    FreeTimeSlots = "FreeTimeSlots",
}

export enum ContentAction {
    VIEW = "VIEW",
    DELETE = "DELETE",
    MARKASREAD = "MARKASREAD",
    MARKASUNREAD = "MARKASUNREAD",
    MARKASUSERPROCESSED = "MARKASUSERPROCESSED",
    MARKASUSERUNPROCESSED = "MARKASUSERUNPROCESSED",
    MARKASFOCUS = "MARKASFOCUS",
    MARKASUNFOCUS = "MARKASUNFOCUS",
}

// ============================================================================
// TOOL IMPLEMENTATION
// ============================================================================

// ============================================================================
// TOOL
// ============================================================================

export type Tool = {
    type: string;
    function: {
        name: string;
        description: string;
        parameters: {
            type: string;
            properties: any;
            required: string[];
        };
    };
};

/**
 * Paramètres pour l'invocation de l'agent d'analyse
 */
export type AgentInvokeParams = {
    /** Historique des messages de la conversation utilisateur */
    userHistory: Message[];
    /** Type d'analyse à effectuer */
    type: string;
    /** Instructions spécifiques pour l'analyse */
    instructions: string;
    /** Données de contenu à analyser (mails minifiés) */
    contentData: MinifiedMail[] | MinifiedAgenda[];
    /** Identifiant unique de session pour cette analyse */
    sessionId: string;
    // Tools
    availableTools: Tool[];
};

// ============================================================================
// ToolImplementation
// ============================================================================

export type ToolImplementation = {
    [key: string]: (args: any) => Promise<any> | any;
};

// ============================================================================
// SEARCH MAILS TOOL
// ============================================================================

/**
 * Analysis request configuration for post-processing agent
 */
type AnalysisRequest = {
    /** Enable post-processing analysis agent */
    enabled: boolean;
    /** Type of analysis requested */
    type?:
        | "synthesis"
        | "extraction"
        | "analysis"
        | "summary"
        | "categorization"
        | "timeline"
        | "statistics"
        | "comparison"
        | "action_items"
        | "custom";
    /** Specific instruction for the analysis agent */
    instruction?: string;
};

/**
 * Parameters for searching emails
 */
export type SearchMailsParams = {
    /** List of keywords to search in email content */
    keywords?: string[];
    /** User descriptions for search context */
    userDescriptions?: string[];
    /** Start date for search (ISO format) */
    startDate?: string;
    /** End date for search (ISO format) */
    endDate?: string;
    /** Maximum number of results to return */
    limit?: number;
    /** Filter by sender addresses */
    from?: string[];
    /** Filter by recipient addresses */
    to?: string[];
    /** Filter by email categories */
    categories?: string[];
    /** Filter by email status */
    mailStatus?: string[];
    /** Filter by read status */
    readStatus?: string;
    /** Filter by attachment presence */
    hasAttachment?: boolean;
    /**
     * Contrat par étapes : la boîte lue par `lireMails`. Absente ou « recus » : la recherche habituelle ;
     * « envoyes » : le dossier Envoyés, et lui seul.
     */
    boite?: "recus" | "envoyes";
    /** Sort order for results */
    sortOrder: CustomViewSortOrder;
    /** AI comment to accompany results */
    aiComment: string;
    /** User action type */
    userAction: string;
    /**
     * Analysis request configuration for triggering post-processing agent. Absent from an Api-Ai
     * answer: no analysis (F-0.8).
     */
    analysisRequest?: AnalysisRequest;
};

export type MinifiedMail = {
    subject: string;
    optimizedUniqueBody: string;
    /** Indique si l'email a été lu */
    isRead: boolean;
    /** Expéditeur de l'email */
    from: MailSender;
    /** Liste des destinataires */
    to: MailSender[];
    /** Liste des destinataires copie */
    cc: MailSender[];
    /** Liste des destinataires copie */
    bcc: MailSender[];
    /** Date et heure d'envoi */
    sentDateTime: IDateValue;
    /** Catégories détectées pour cet email */
    categories?: string[];
    /** Liste des tâches à faire détectées */
    toDo?: TodoItem[];
    /** Indique si le mail */
    isFocus: boolean;
    /** SmartLabel si il en possède un */
    smartLabel?: SmartLabel;
};

// ============================================================================
// APPLY ACTION TO PREVIOUS RESULTS TOOL
// ============================================================================

/**
 * Parameters for applying an action to a previously displayed result set
 * (referenced by its short alias "Rx" emitted in the conversation history)
 */
export type ApplyActionToPreviousResultsParams = {
    /** Alias of the targeted result set, format "Rx" (ex: "R1") */
    targetRef: string;
    /** Action to apply — same enum as SearchMailsParams.userAction */
    userAction: string;
    /** AI comment to accompany the action */
    aiComment: string;
};

/**
 * Entry of the result set cache kept by the ConversationManager.
 * Stores the EXACT uniqueMailvistaIds (`internetMessageId::parentFolderId`) of the copies
 * returned to the component so a later applyActionToPreviousResults can target them
 * without re-running a search.
 */
export type ResultSetCacheEntry = {
    /** UUID of the originating ComponentData */
    userRequestId: string;
    /** Exact uniqueMailvistaIds of the copies returned to the component (what the user sees) */
    uniqueMailvistaIds: string[];
    /** Filter actually executed (enriched keywords included) — degraded fallback only */
    filter: ViewFilter;
    /** Content type of the result set (Mails in V1) */
    contentType: ComponentDataContentType;
    /** Reference block "[ref:Rx | type:mails | n:N | filtres: ...]" appended to the history */
    refBlock: string;
};

// ============================================================================
// FALLBACK RESPONSE TOOL
// ============================================================================

/**
 * Parameters for fallback response when no specific action is possible
 */
export type FallbackResponseParams = {
    /** Main AI message */
    aiComment: string;
    /** Alternative suggestion provided by AI */
    aiSuggestion: string;
};

// ============================================================================
// SUGGEST AVAILABLE TOOLS
// ============================================================================

/**
 * Parameters for suggesting available tools with descriptions
 */
export type SuggestAvailableToolsParams = {
    /** Main AI message */
    aiComment: string;
    /** Names of tools to suggest */
    availableToolNames: string[];
};

// ============================================================================
// BASIC AI CHAT TOOL
// ============================================================================

/**
 * Parameters for basic AI chat response without specific content
 */
export type BasicAiChatParams = {
    /** Text message from AI to display */
    aiComment: string;
};

// ============================================================================
// CREATE AGENDA ITEM TOOL
// ============================================================================

/**
 * Parameters for creating a new calendar event
 */
export type CreateAgendaItemParams = {
    /** Event title/subject */
    subject: string;
    /** Start date and time (ISO format) */
    start: string;
    /** End date and time (ISO format) */
    end: string;
    /** Event description (optional) */
    description?: string;
    /** Event location (optional) */
    location?: string;
    /** List of attendees (optional) */
    attendees?: string[];
    /** AI comment */
    aiComment: string;
    /** AI comment */
    isAllDay?: boolean;
};

// ============================================================================
// GET AGENDA ITEMS TOOL
// ============================================================================

/**
 * Parameters for retrieving calendar events for a given period
 */
export type GetAgendaItemsParams = {
    /** Start date of the period (ISO format) */
    startDate: string;
    /** End date of the period (ISO format) */
    endDate: string;
    /** Filter by attendees (optional) */
    attendees?: string[];
    /** Maximum number of results (optional) */
    limit?: number;
    /** Sort order (optional) */
    orderBy?: CustomViewSortOrder;
    /** Keywords to search for (optional) */
    keywords?: string[];
    /** AI comment */
    aiComment: string;
    /**
     * Analysis request configuration for triggering post-processing agent. Absent from an Api-Ai
     * answer: no analysis (F-0.8).
     */
    analysisRequest?: AnalysisRequest;
};

export type MinifiedAgenda = {
    subject: string;
    bodyPreview: string;
    start: string;
    end: string;
    location?: AgendaItemLocation;
    attendees: AgendaItemAttendee[];
    organizer?: AgendaItemOrganizer;
    onlineMeeting?: OnlineMeeting;
    isAllDay: boolean;
    isCancelled: boolean;
};

// ============================================================================
// FIND FREE TIME SLOTS TOOL
// ============================================================================

/**
 * Parameters for finding free time slots in the agenda
 */
export type FindFreeSlotsParams = {
    /** Start date for search (ISO format) */
    startDate: string;
    /** End date for search (ISO format) */
    endDate: string;
    /** Minimum duration of slots in minutes */
    minutesToAllocate: number;
    /** AI comment */
    aiComment: string;
};

// ============================================================================
// COMPONENT DATA
// ============================================================================
type MoreDataItem = Mail[] | AgendaTimeSlot[] | AgendaItem[];

export type MoreDataCallbackResult = {
    userRequestId: string;
    content: MoreDataItem;
    moreDataCallBack?: () => Promise<MoreDataCallbackResult>;
};

/**
 * Cause d'un tour raté (F-1.17) :
 *  - `quota` : quota d'assistant épuisé (l'app ne propose pas « Réessayer ») ;
 *  - `network` : réseau coupé, serveur injoignable ;
 *  - `service` : toute autre panne (Api-Backend, Api-Ai, Graph, Google).
 */
export type TurnErrorCause = "quota" | "network" | "service";

/**
 * Contrat entre l'assistant et l'Api-Ai (option du SDK) :
 *  - `v1` (par défaut) : un appel par tour, l'Api-Ai rend un `toolCall` ;
 *  - `etapes` : contrat par étapes (docs/contrat-etapes.md de l'Api-Ai) — l'Api-Ai demande des
 *    lectures, l'app les exécute et rappelle, jusqu'à la fin du tour.
 */
export type ConversationContract = "v1" | "etapes";

/**
 * Une ligne du raisonnement d'un tour (contrat par étapes, quand l'Api-Ai l'envoie) :
 *  - `thought` : le raisonnement du modèle, tel quel ;
 *  - `step` : ce que l'assistant a fait (lecture, calcul, garde), écrit par l'Api-Ai.
 * À afficher seulement : jamais renvoyé à l'Api-Ai, jamais dans l'historique.
 */
export type ReasoningItem = { kind: "thought" | "step"; text: string };

/**
 * Place occupée par la conversation (contrat par étapes), pour la jauge de l'app : ce que le dernier
 * appel de l'assistant a lu et écrit, sur le maximum fixé par l'Api-Ai. Mesurée par le fournisseur,
 * en tokens. Inconnue avant le premier tour et juste après un compactage.
 */
export type ConversationContextSize = {
    used: number;
    max: number;
    /**
     * `used` découpé par ce qui l'occupe, estimé par l'Api-Ai (le fournisseur ne donne que le total) :
     * la somme vaut `used`. Absent quand l'Api-Ai ne l'envoie pas.
     */
    parts?: ConversationContextParts;
};

/**
 * Ce que l'assistant a consommé (contrat par étapes), en tokens lus et écrits, comptés par le
 * fournisseur et additionnés par l'Api-Backend : la conversation en cours depuis son premier tour, et
 * l'utilisateur depuis le début du mois civil. À la différence de la place occupée, ces nombres ne
 * redescendent jamais : chaque question relit toute la conversation, et la relecture compte.
 */
export type AssistantUsage = {
    conversationTokens: number;
    monthTokens: number;
    /**
     * Le 100 % de la jauge du mois, fixé par l'Api-Backend : un repère de coût, rien n'est bloqué
     * au-delà. Absent quand l'Api-Backend ne le donne pas.
     */
    monthMaxTokens?: number;
    /** Début du mois compté (ISO, UTC). */
    monthStart: string;
};

/** Ce qui occupe la conversation, en tokens estimés. */
export type ConversationContextParts = {
    /** Les consignes et la description des outils, relues à chaque question. */
    instructions: number;
    /** Les questions, les réponses et les propositions. */
    exchanges: number;
    /** Ce que l'app a rapporté des mails et de l'agenda lus. */
    reads: number;
    /** La réflexion de l'assistant. */
    reasoning: number;
};

/**
 * Le modèle de l'assistant choisi par l'utilisateur (contrat par étapes) : « auto » laisse l'Api-Ai
 * décider selon la demande ; « small » ou « medium » force Mistral Small ou Mistral Medium.
 */
export type AgentModelChoice = "auto" | "small" | "medium";

/** Le modèle qui a écrit une réponse (contrat par étapes), à afficher sous elle, et s'il a raisonné. */
export type AnswerModel = { name: string; reasoning: boolean };

/** Une conversation de la liste de l'historique (contrat par étapes). */
export type ConversationSummary = ConversationSummaryDTO;

/**
 * Un projet de l'assistant : un espace de l'utilisateur où ranger des conversations, avec ses consignes
 * (suivies dans chacune) et son périmètre de mails (les adresses auxquelles les lectures sont limitées).
 */
export type AssistantProject = AssistantProjectDTO;

/** Ce qu'on donne pour créer un projet ; pour le modifier, les seuls champs à changer. */
export type AssistantProjectInput = AssistantProjectInputDTO;

/** Ce que l'app réaffiche d'une conversation rouverte, dans l'ordre. */
export type RestoredConversationItem =
    | { kind: "turn"; question: string; response: ComponentData }
    | { kind: "compaction"; summary: string; at: string };

/** Une conversation rouverte : la suite de la conversation repart de là. */
export type RestoredConversation = {
    id: string;
    title: string | null;
    items: RestoredConversationItem[];
    /** Les pouces déjà donnés, par identifiant de tour (`ComponentData.turnId`). */
    feedback: Record<string, AnswerFeedback>;
    /** Le projet où la conversation est rangée, s'il y en a un. */
    project?: AssistantProject;
};

/** Pouce sous une réponse (contrat par étapes) : utile ou pas. */
export type AnswerFeedback = "up" | "down";

/**
 * La réponse de l'assistant (contrat par étapes) : du texte en Markdown simple, où chaque ref citée
 * entre crochets ([m3], [e2], [c1]) désigne un élément lu ou calculé pendant la conversation, donné
 * ici par sa ref. L'app remplace chaque ref connue par l'élément (`splitAnswer` : une ligne de ref
 * seules, les éléments en entier ; une ref dans une phrase, une pastille) ; une ref inconnue reste
 * du texte. Rien de ce que le modèle écrit ne se charge ni ne s'ouvre : seuls ces éléments sont
 * cliquables. `draft` : le brouillon de mail préparé, montré après la réponse.
 */
export type AssistantAnswer = {
    text: string;
    mails: Record<string, Mail>;
    /** Refs des mails de `mails` que l'utilisateur a envoyés : l'affichage montre leurs destinataires. */
    sentMails?: string[];
    /** Une question à choix, montrée après la réponse : cliquer un choix envoie sa `reply`, rien de plus. */
    question?: AssistantQuestion;
    /**
     * Par ref de `slots` : le message à envoyer quand l'utilisateur clique le créneau, comme s'il l'avait
     * tapé (« Je prends le créneau du mardi 06/10, 14:00 → 16:00 »). Cliquer choisit, rien n'est créé.
     */
    slotReplies?: Record<string, string>;
    events: Record<string, AgendaItem>;
    slots: Record<string, AgendaTimeSlot>;
    draft?: AssistantDraft;
    /**
     * Les noms demandés que les contacts n'ont pas trouvés, et que la lecture a cherchés tels quels dans
     * les adresses : l'app le dit en tête de la réponse, à partir de sa recherche, sans le modèle
     * (docs/refonte-assistant/CONCEPTION-nom-introuvable.md de l'Api-Ai). Absent sans nom introuvable.
     */
    unknownNames?: AssistantUnknownName[];
};

/**
 * Un nom introuvable dans les contacts : tel que l'Api-Ai l'a demandé, et où il a été cherché — dans les
 * expéditeurs (`sender`), ou dans les destinataires des mails envoyés (`recipient`).
 */
export type AssistantUnknownName = {
    name: string;
    role: "sender" | "recipient";
};

/**
 * Une question à choix posée à l'utilisateur (1 à 4 choix, le plus probable en premier). `reply` : le
 * message à envoyer quand l'utilisateur clique le choix, comme s'il l'avait tapé. L'utilisateur peut
 * toujours écrire autre chose : rien ne reste bloqué.
 */
export type AssistantQuestion = {
    question: string;
    choices: { label: string; detail?: string; reply: string }[];
};

/**
 * Un brouillon de mail préparé par l'assistant, en texte brut, que l'utilisateur copie ou ouvre dans
 * l'éditeur de mail pour le relire et l'envoyer lui-même. `replyTo` : le mail auquel il répond
 * (l'éditeur en reprend les destinataires, l'objet et le fil, comme le bouton Répondre) ; sans lui, un
 * nouveau mail d'objet `subject`, sans destinataire. L'assistant ne choisit jamais de destinataire.
 */
export type AssistantDraft = {
    body: string;
    subject?: string;
    replyTo?: Mail;
};

export type ComponentData = {
    aiComment: string;
    userRequestId: string;
    content: {
        contentData:
            | Mail[]
            | string
            | AgendaItem[]
            | CreateAgendaItemData
            | AgendaTimeSlot[];
        contentType: ComponentDataContentType;
        contentAction: ContentAction | undefined;
        upcomingAnalysis: boolean;
        moreDataCallBack?: () => Promise<MoreDataCallbackResult>;
    };
    aiVocalText: string;
    /**
     * Tour raté seulement (`contentType` `Error`) : sa cause. Le tour n'a rien laissé dans
     * l'historique. Absent sur un tour réussi.
     */
    errorCause?: TurnErrorCause;
    /** Raisonnement du tour, dans l'ordre (contrat par étapes). Absent si l'Api-Ai n'en envoie pas. */
    reasoning?: ReasoningItem[];
    /**
     * Contrat par étapes : le modèle qui a écrit la réponse, à afficher sous elle. Absent quand le code
     * l'a écrite (salutation, repli) ou sur un tour raté.
     */
    answerModel?: AnswerModel;
    /**
     * Contrat par étapes, tour réussi : l'identifiant du tour (le X-Mv-Trace-Id de ses appels), pour
     * le pouce sous la réponse (`sendAnswerFeedback`). Absent sur un tour raté.
     */
    turnId?: string;
    /**
     * Contrat par étapes : la réponse de l'assistant, une seule par tour, affichée à la place de
     * `aiComment` ; `content` porte alors la proposition à confirmer (mails d'une action, rendez-vous),
     * montrée après elle, et `answer.draft` un brouillon de mail. Absent (contrat v1, réponse fixe) :
     * `aiComment` et `content`, comme avant.
     */
    answer?: AssistantAnswer;
    /**
     * Tour annulé (« Nouvelle conversation » pendant le tour) : rien à afficher, rien dans
     * l'historique. L'app ignore la réponse.
     */
    cancelled?: boolean;
    /**
     * Tour arrêté par l'utilisateur (`stopTurn`), avec `cancelled` : la question est sortie de
     * l'historique, comme pour un tour raté ; l'app peut la remettre dans la barre de saisie.
     */
    stopped?: boolean;
};
